// Packages
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

// Store
import { useAppSelector } from "../store";

// Types
import {
  GetDcaStrategySummaryApiResponse,
  DcaStrategyDto,
  DcaStrategyReqDto,
} from "./api/generated";

type modalStatesType = "create plan" | "summary" | "success";

export interface DcaState {
  frequency: DcaStrategyReqDto["frequency"];
  amount: number;
  sellCurrency?: string;
  buyCurrency?: string;
  startDate: number;
  endDate?: number;
  spendLimit?: number;
  summary: GetDcaStrategySummaryApiResponse | null;
  modalState: modalStatesType;
  errorMessage?: string;
  spendLimitError?: string;
  selectedStrategy?: DcaStrategyDto;
}

const startDate = new Date().getTime();

const initialState: DcaState = {
  frequency: "Week",
  startDate,
  endDate: undefined,
  summary: null,
  modalState: "create plan",
  buyCurrency: "BTC",
  amount: 0,
  spendLimit: undefined,
};

const DcaSlice = createSlice({
  name: "dca",
  initialState: initialState,
  reducers: {
    setFrequency: (state, { payload }: PayloadAction<DcaStrategyReqDto["frequency"]>) => {
      state.frequency = payload;
    },
    setSellFiatAmount: (state, { payload }: PayloadAction<DcaStrategyReqDto["amount"]>) => {
      state.amount = payload;
    },
    setSellCurrency(state, { payload }: PayloadAction<string>) {
      state.sellCurrency = payload;
    },
    setBuyCurrency(state, { payload }: PayloadAction<string>) {
      state.buyCurrency = payload;
    },
    setStartDate(state, { payload }: PayloadAction<DcaStrategyReqDto["startDate"]>) {
      state.startDate = payload;
    },
    setEndDate(state, { payload }: PayloadAction<number>) {
      state.endDate = payload;
    },
    setSpendLimit(state, { payload }: PayloadAction<number>) {
      state.spendLimit = payload;
    },
    resetLimitValues(state) {
      state.endDate = undefined;
      state.spendLimit = undefined;
      state.spendLimitError = undefined;
    },
    setModalState(state, { payload }: PayloadAction<modalStatesType>) {
      state.modalState = payload;
    },
    setErrorMessage(state, { payload }: PayloadAction<string>) {
      state.errorMessage = payload;
    },
    setSpendLimitError(state, { payload }: PayloadAction<string>) {
      state.spendLimitError = payload;
    },
    setSelectedStrategy(state, { payload }: PayloadAction<DcaStrategyDto>) {
      state.selectedStrategy = payload;
    },
    setSummaryResponse(state, { payload }: PayloadAction<GetDcaStrategySummaryApiResponse>) {
      state.summary = payload;
    },
    resetDcaState: () => {
      return { ...initialState };
    },
  },
});

export const {
  setFrequency,
  setSellFiatAmount,
  setSellCurrency,
  setBuyCurrency,
  setSummaryResponse,
  setStartDate,
  setEndDate,
  setSpendLimit,
  resetLimitValues,
  setModalState,
  resetDcaState,
  setErrorMessage,
  setSelectedStrategy,
  setSpendLimitError,
} = DcaSlice.actions;

export const useDcaSlice = () => useAppSelector((state) => state.dca);

export default DcaSlice;
